import {
  ITaskPrefill,
  TASK_PREFILL_FIELDS,
  TaskModel,
  TaskParameterModel,
  ITaskListingModel
} from "../models/task";

import axios, { AxiosPromise } from "axios";
import { GenericBaseParameter } from "../models/base-parameter";
import { GenericServices } from "./generic.services";
import { taskUri } from "../common";
import { ApiResponse } from "../models/app-configuration";
import { Listing } from "../models/listing-base-model";

export class TaskServices extends GenericServices {
  // @ts-ignore
  selectPost<TValue, TResult>(
    params: GenericBaseParameter<TaskParameterModel>
  ): AxiosPromise<TResult> {
    params.Fields = "*";
    return this.executeSelectingPost(params, taskUri.search);
  }

  select<TValue, TResult>(
    params: TValue,
    path?: string,
    config?: any
  ): AxiosPromise<TResult> {
    return this.executeSelecting(params, path, true, config);
  }

  /**
   * Resolve the user/person/property of a task for the agenda prefetch.
   * List rows are field projections that may miss UserId/PersonId/PropertyId,
   * so the task preview is loaded; the row fields are the fallback on failure.
   */
  async getAgendaPrefetchFields(task: any): Promise<{
    userId: number;
    personId: number;
    personReference: string;
    propertyId: number;
    propertyReference: string;
  }> {
    try {
      const response = (await this.select(
        { id: task.Id },
        taskUri.preview.replace(":id", String(task.Id))
      )) as ApiResponse;
      if (response.isSuccess && response.data) {
        const detail = response.data;
        return {
          userId: detail.UserId,
          personId: detail.PersonId,
          personReference: detail.PersonInfo && detail.PersonInfo.Reference,
          propertyId: detail.PropertyInfo && detail.PropertyInfo.Id,
          propertyReference: detail.PropertyInfo && detail.PropertyInfo.Reference
        };
      }
    } catch (err) {
      console.log(err);
    }
    return {
      userId: task.UserId,
      personId: task.PersonId,
      personReference: task.PersonReference,
      propertyId: task.PropertyId,
      propertyReference: task.PropertyReference
    };
  }

  /**
   * Load a task as the seed for a new one (FRO-11391). The detail is read rather
   * than the preview: it answers the model's own field names, so TASK_PREFILL_FIELDS
   * is a straight copy. System state (id, status, audit, recurrence) stays behind.
   *
   * Never rejects: an unreadable task answers an empty prefill, so the caller still
   * opens a task form.
   */
  async getPrefillFromTask(taskId: number): Promise<ITaskPrefill> {
    const prefill: ITaskPrefill = {};
    try {
      const response = (await this.select(
        null,
        taskUri.paramId.replace(":id", String(taskId))
      )) as ApiResponse;
      if (response.isSuccess && response.data) {
        const task: TaskModel = response.data;
        // Writing through a key union needs the widened target. The API omits null
        // fields, so an absent one must stay absent rather than overwrite the new
        // task's own default.
        const seeded = prefill as Record<string, any>;
        TASK_PREFILL_FIELDS.forEach(field => {
          const value = task[field];
          if (value !== null && value !== undefined) seeded[field] = value;
        });
      }
    } catch (err) {
      console.log(err);
    }
    return prefill;
  }

  selectAsync<TValue, TResult>(
    params: TValue,
    path?: string
  ): AxiosPromise<TResult[]> {
    return this.executeSelecting(params, path);
  }
  insertTask<TResult>(model: TaskModel): AxiosPromise<TResult> {
    return this.executeCreating(model, taskUri.default).then((response: ApiResponse) => {
      return response.isSuccess
        ? Promise.resolve(response.data)
        : Promise.reject(response)
    })
      .catch(error => Promise.reject(error));
  }

  updateTask<TResult>(
    originEntity: TaskModel,
    entity: TaskModel
  ): AxiosPromise<TResult> {
    const clonedOriginEntity = JSON.parse(JSON.stringify(originEntity));
    const clonedEntity = JSON.parse(JSON.stringify(entity));

    let path = taskUri.paramId.replace(":id", entity.Id.toString());
    return this.executeUpdatingCompare(clonedOriginEntity, clonedEntity, path).then((response: ApiResponse) => {
      return response.isSuccess
        ? Promise.resolve(response.data)
        : Promise.reject(response)
    })
      .catch(error => Promise.reject(error));
  }
  selectBy<TValue, TResult>(
    value: TValue,
    path?: string
  ): AxiosPromise<TResult> {
    return this.executeSelectBy(value, path);
  }
  deleteTask<TResult>(ids: number[]): AxiosPromise<TResult> {
    let path = taskUri.paramId.replace(":id", ids.join(","));
    return this.executeDeleting(path)
      .then((response: ApiResponse) => {
        return response.isSuccess
          ? Promise.resolve(response.data)
          : Promise.reject(response.message);
      })
      .catch(error => Promise.reject(error));
  }
  selectByAsync<TValue, TResult>(
    value: TValue,
    path?: string
  ): AxiosPromise<TResult> {
    return this.executeSelectByAsync(value, path);
  }
  getListOfTasks(param: GenericBaseParameter<TaskParameterModel>, cancelTokenSource?: any) {
    return super
      .executeSelectingPost(param, taskUri.search, cancelTokenSource)
      .then((response: ApiResponse) => {
        // executeSelectingPost resolves with axios errors instead of rejecting;
        // pass a superseded search on as a cancellation.
        if (axios.isCancel(response)) {
          return Promise.reject(response);
        }
        if (response.isSuccess) {
          return Promise.resolve(
            new Listing<ITaskListingModel>("listOfTasks", response.data)
          );
        } else return Promise.reject(response.message);
      })
      .catch(ex => {
        console.log(ex);
        return Promise.reject(ex);
      });
  }

  initTask(model: TaskModel): AxiosPromise<TaskModel> {
    return this.executeSelectingPost(model, taskUri.init);
  }


  setTouchTasks(taskIds: number[]): AxiosPromise<any> {
    let path = taskUri.touch.replace(":ids", taskIds.join(","));
    return this.executePut(null, path);
    }
    
    quickUpdateTask(id: number, payload) {
        const params = []
        Object.keys(payload).forEach(key => {
            params.push({
                op: "replace",
                path: `/${key}`,
                value: payload[key]
            })
        })
        let path = taskUri.paramId.replace(":id", id.toString());
        return this.executePatch(params, path).then((response: ApiResponse) => {
            if (response.data && response.data.isSuccess) {
                return Promise.resolve(true);
            } else return Promise.reject(response.data && response.message);
        }).catch(ex => {
            return Promise.reject(ex);
        });
    }

    updateTaskStatus(id: number, isDone: boolean) {
        const payload = [{
            op: "replace",
            path: "/IsDone",
            value: isDone
        }]
        let path = taskUri.paramId.replace(":id", id.toString());
        return this.executePatch(payload, path).then((response: ApiResponse) => {
            if (response.data && response.data.isSuccess) {
                return Promise.resolve(true);
            } else return Promise.reject(response.data && response.message);
            }).catch(ex => {
                    return Promise.reject(ex);
            });
    }

    deleteAll(payload) {
        return new Promise((resolve, reject) => {
            return super.executeDeletingWith(payload, taskUri.deleteAll)
                .then((response: ApiResponse) => {
                    return response.isSuccess ? resolve(response.data) : reject(response.message);
                }).catch(error => {
                    return reject(error)
                })
        })
    }

    createTaskEvaluation(payload) {
        return super.executeSelectingPost(payload, taskUri.createTaskEvaluation)
            .then((response: ApiResponse) => {
                if (response.isSuccess) {
                    return Promise.resolve(response.data);
                } else return Promise.reject(response.message);
            })
            .catch(ex => {
                console.log(ex);
                return Promise.reject(ex);
            });
    }

    createTaskCond(payload) {
        return super.executeSelectingPost(payload, taskUri.createTaskCondition)
            .then((response: ApiResponse) => {
                if (response.isSuccess) {
                    return Promise.resolve(response.data);
                } else return Promise.reject(response.message);
            })
            .catch(ex => {
                console.log(ex);
                return Promise.reject(ex);
            });
    }

  constructor() {
    super();
  }
}
